import { useContext, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { toPrice } from '../../../../utils/parsers';
import { Button } from '../../../general/Button';
import { Account, AccountType, PensionContext } from '../../../../contexts/pension-robot/PensionContext';
import { SupplementGeneralModal } from '../../supplement-overlay/SupplementGeneralModal';
import { usePostDialogueData } from '../../../../api/pension-robot/usePostPensionDialogueData';
import { RobotEnvironmentContext } from '../../../../contexts/general/RobotEnvironmentContext';
import { getPerMonthFactor, isMyBND, riskProfileById, toFullEurosAsCents } from '../../../../utils/functions';
import { useAccountBalancePrediction } from '../../hooks/AccountBalancePredictions';
import { DateTime } from 'luxon';
import {
    DEFAULT_PENSION_AGE,
    MIN_AGE_CLIENT,
    RiskProfile,
    RiskProfileId,
    riskProfiles,
} from '../../../../config/constants';
import { useGetPerMonthFactorValues } from '../../../../hooks/useGetPerMonthFactorValues';

export const SaveOrInvest: React.FC<{
    accountType: AccountType;
}> = ({ accountType }) => {
    const {
        actions,
        parameters: { supplementInfo },
    } = useContext(PensionContext);

    const [showModal, setShowModal] = useState(false);

    const { environment } = useContext(RobotEnvironmentContext);
    const query = usePostDialogueData();

    useEffect(() => {
        isMyBND(environment) && query.refetch();
    }, [supplementInfo?.self?.additionalInvestmentAccount, supplementInfo?.self?.additionalSavingAccount]); // eslint-disable-line react-hooks/exhaustive-deps

    const getAccount = (accountType: AccountType) => {
        if (accountType === 'investment')
            return supplementInfo?.self?.additionalInvestmentAccount?.isUsed
                ? supplementInfo?.self?.additionalInvestmentAccount?.account
                : ({
                      type: 'investment',
                      recurringPaymentInterval: 'month',
                      riskProfile: riskProfiles.neutral,
                  } as Account);
        if (accountType === 'saving')
            return supplementInfo?.self?.additionalSavingAccount?.isUsed
                ? supplementInfo?.self?.additionalSavingAccount?.account
                : ({ type: 'saving', recurringPaymentInterval: 'month' } as Account);
        return undefined;
    };

    const addAccount = (account: Account) => {
        if (account.type === 'investment')
            actions.setParameters({
                supplementInfo: {
                    self: {
                        additionalInvestmentAccount: {
                            account: {
                                ...account,
                                predictedAmountAtEndAccount: toFullEurosAsCents(account.predictedAmountAtEndAccount),
                            },
                            isUsed: true,
                        },
                    },
                },
            });

        if (account.type === 'saving')
            actions.setParameters({
                supplementInfo: {
                    self: {
                        additionalSavingAccount: {
                            account: {
                                ...account,
                                predictedAmountAtEndAccount: toFullEurosAsCents(account.predictedAmountAtEndAccount),
                            },
                            isUsed: true,
                        },
                    },
                },
            });
    };

    const currentAdditionalAccount = getAccount(accountType);
    const hasAdditionalAccount =
        accountType === 'investment'
            ? supplementInfo?.self?.additionalInvestmentAccount?.isUsed
            : supplementInfo?.self?.additionalSavingAccount?.isUsed;

    return (
        <div className="p-4 pb-8 border-b flex flex-col gap-6">
            {!hasAdditionalAccount && (
                <StartInformation
                    setShowModal={setShowModal}
                    currentAccountActive={!!hasAdditionalAccount}
                    accountType={accountType}
                />
            )}
            {hasAdditionalAccount && currentAdditionalAccount && (
                <AddedInformation currentAccount={currentAdditionalAccount} setShowModal={setShowModal} />
            )}
            {showModal && !!currentAdditionalAccount && (
                <SupplementGeneralModal
                    onlyAvailableAtPensionDate={true}
                    isPartnerSection={false}
                    account={currentAdditionalAccount}
                    onClose={() => setShowModal(false)}
                    isShown={showModal}
                    updateAccount={addAccount}
                    isResultSection={true}
                />
            )}
        </div>
    );
};

const StartInformation: React.FC<{
    setShowModal: (isShown: boolean) => void;
    currentAccountActive: boolean;
    accountType: AccountType;
}> = ({ setShowModal, currentAccountActive, accountType }) => {
    const { t } = useTranslation('pension');
    const buttonText =
        accountType === 'investment'
            ? t('pension-cocktail-fill-screen-results-save-or-invest-btn-invest')
            : t('pension-cocktail-fill-screen-results-save-or-invest-btn-save');

    const subText =
        accountType === 'investment'
            ? t('pension-cocktail-fill-screen-results-invest-text')
            : t('pension-cocktail-fill-screen-results-save-text');

    const SetAccountButton = () => {
        return (
            <Button
                className={`w-48`}
                buttonType="primary"
                disabled={currentAccountActive}
                onClick={() => {
                    setShowModal(true);
                }}
            >
                {buttonText}
            </Button>
        );
    };

    return (
        <div className="flex flex-col gap-4 sm:gap-6">
            <div>{subText}</div>
            <div className="rounded-lg py-5 flex flex-row flex-wrap gap-2 sm:gap-4 justify-start">
                <SetAccountButton />
            </div>
        </div>
    );
};

const AddedInformation: React.FC<{
    setShowModal: (isShown: boolean) => void;
    currentAccount: Account;
}> = ({ setShowModal, currentAccount }) => {
    const { t } = useTranslation('pension');
    const { actions, parameters } = useContext(PensionContext);
    const { computationResultAtPension } = useAccountBalancePrediction(currentAccount, true);
    const forecastRetirementQuery = useGetPerMonthFactorValues(parameters.personalInfo.self?.age ?? MIN_AGE_CLIENT);
    const perMonthFactor = getPerMonthFactor(forecastRetirementQuery.data);
    const resultMonthly = computationResultAtPension / perMonthFactor;

    const onRemoveAccount = () => {
        if (currentAccount.type === 'investment')
            actions.setParameters({ supplementInfo: { self: { additionalInvestmentAccount: { isUsed: false } } } });
        else actions.setParameters({ supplementInfo: { self: { additionalSavingAccount: { isUsed: false } } } });
    };

    const getRiskProfileDescription = (id: RiskProfile) => {
        const obj = {
            veryDefensive: t('general-very-defensive'),
            defensive: t('general-defensive'),
            neutral: t('general-neutral'),
            offensive: t('general-offensive'),
            veryOffensive: t('general-very-offensive'),
        };
        return obj[id];
    };

    const retirementAge = parameters.personalInfo.self?.pensionAge ?? DEFAULT_PENSION_AGE;
    const retirementYear = DateTime.now().year + (retirementAge - (parameters.personalInfo.self?.age ?? 0));

    return (
        <div className="flex flex-col gap-4">
            <div className="rounded-lg bg-bnd-white-1 shadow-xl p-5 flex flex-col justify-start">
                <div className="flex flex-row justify-between ">
                    <div className="font-medium">
                        {t('pension-cocktail-fill-screen-results-save-or-invest-info-exp-returns')}
                    </div>
                </div>
                <div className="my-4 flex flex-col">
                    <div>
                        <Trans
                            t={t}
                            i18nKey={'pension-cocktail-fill-screen-results-save-or-invest-info-start-investment'}
                            values={{
                                value: toPrice(currentAccount.currentAmount ?? 0, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                }),
                            }}
                        >
                            ... <span className="font-medium">value</span> ...
                        </Trans>
                    </div>
                    <div>
                        <Trans
                            t={t}
                            i18nKey={'pension-cocktail-fill-screen-results-save-or-invest-info-investment'}
                            values={{
                                value: toPrice(currentAccount.recurringPaymentAmount ?? 0, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                }),
                                period:
                                    currentAccount.recurringPaymentInterval === 'month'
                                        ? t('general-per-month-lower')
                                        : t('general-per-year-lower'),
                            }}
                        >
                            ... <span className="font-medium">value</span> ...
                        </Trans>
                    </div>
                    {currentAccount.type === 'investment' && (
                        <div>
                            <Trans
                                t={t}
                                i18nKey={'pension-cocktail-fill-screen-results-save-or-invest-info-risk-strategy'}
                                values={{
                                    value: getRiskProfileDescription(
                                        riskProfileById(currentAccount.riskProfile as RiskProfileId) as RiskProfile,
                                    ),
                                }}
                            >
                                ... <span className="font-medium">value</span> ...
                            </Trans>
                        </div>
                    )}
                </div>
                <div className="flex flex-col">
                    <div className="font-medium">
                        {t('pension-cocktail-fill-screen-results-save-or-invest-info-returns-pension', {
                            year: retirementYear,
                        })}
                    </div>
                    <div>
                        <Trans
                            t={t}
                            i18nKey={'pension-cocktail-fill-screen-results-save-or-invest-info-returns-text'}
                            values={{
                                year:
                                    parameters.personalInfo.self?.pensionAge && !!parameters.personalInfo.self?.age
                                        ? parameters.personalInfo.self?.pensionAge - parameters.personalInfo.self?.age
                                        : 0,
                                totalValue: toPrice(computationResultAtPension, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                }),
                                monthlyValue: toPrice(resultMonthly, {
                                    onlyFullEuros: true,
                                    withCurrencySymbol: true,
                                }),
                            }}
                        >
                            ...
                            <span className="font-medium">year</span>
                            <span className="font-medium">totalValue</span>
                            <span className="font-medium">monthlyValue</span>
                            ...
                        </Trans>
                    </div>
                </div>
                <div className="mt-8 flex flex-row flex-wrap gap-2 sm:gap-4">
                    <Button
                        buttonType="primary"
                        className="w-48"
                        onClick={() => {
                            setShowModal(true);
                        }}
                    >
                        {t('pension-cocktail-fill-screen-results-save-or-invest-info-edit-account')}
                    </Button>
                    <Button buttonType="secondary" className="w-48" onClick={onRemoveAccount}>
                        {t('pension-cocktail-fill-screen-results-save-or-invest-info-remove-account')}
                    </Button>
                </div>
            </div>
        </div>
    );
};
